'use client';

import { IconCloseCircleFilled } from '@zillow/constellation-icons/react/filled/icon-close-circle-filled';
import { Children, cloneElement, forwardRef, isValidElement, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { CloseButton } from '../close-button/close-button';
import { Icon } from '../icon/icon';
import { InputChipContext } from './context/input-chip.context';
import { inputChipCloseStyles } from './styles/input-chip-close.styles';
import type { InputChipClosePropsInterface } from './types/input-chip-close.types';

export const InputChipClose = forwardRef<HTMLDivElement, InputChipClosePropsInterface>(
  (props: InputChipClosePropsInterface, forwardedRef) => {
    const {
      children = (
        <CloseButton>
          <Icon>
            <IconCloseCircleFilled />
          </Icon>
        </CloseButton>
      ),
      className,
      css: cssProp,
      title,
      tabIndex,
      ...rest
    } = props;

    const { disabled, handleClose, labelText } = useContext(InputChipContext);

    const styles = css(inputChipCloseStyles.raw({ disabled: disabled ?? false }), cssProp);

    const content = useMemo(() => {
      const child = Children.toArray(children).at(0);

      if (!isValidElement(child)) {
        return null;
      }

      return cloneElement<any>(child, {
        disabled,
        onClick: (e: any) => {
          child.props.onClick?.(e);
          handleClose?.();
        },
        tabIndex,
        title: title || `Remove ${labelText}`,
      });
    }, [children, handleClose, labelText, title, disabled, tabIndex]);

    return (
      <span
        className={cx('c11n', 'c11n_input-chip__close', styles, className)}
        data-c11n-component="InputChip.Close"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        {...rest}
      >
        {content}
      </span>
    );
  },
);

InputChipClose.displayName = 'InputChip.Close';
